import { logger } from '@heidi/runtime/logger';

import type { TimelineEntry } from '../../../contracts/voice-timeline.types';
import { voiceBuildMode } from '../../../data/voice-build-mode';

export interface VoiceDebugDumpMeta {
  exportedAt: string;
  connection: string;
  sessionState: string;
  surfacePath?: string;
  currentSessionId?: string;
  error?: string | null;
}

/** Local/dev only — expose dump tools (sessionStorage + full log + Download button). */
export function shouldAutoDumpVoiceSession(): boolean {
  return voiceBuildMode().isDebugDumpEnvironment;
}

/** Format a chronological voice session dump for local debug (downloadable .txt). */
export function formatVoiceDebugDump(
  timeline: TimelineEntry[],
  meta: VoiceDebugDumpMeta
): string {
  const lines: string[] = [
    'Talk to Heidi — session dump',
    `exported_at: ${meta.exportedAt}`,
    `connection: ${meta.connection}`,
    `session_state: ${meta.sessionState}`,
  ];
  if (meta.surfacePath) lines.push(`route: ${meta.surfacePath}`);
  if (meta.currentSessionId) lines.push(`current_session_id: ${meta.currentSessionId}`);
  if (meta.error) lines.push(`error: ${meta.error}`);
  lines.push('', '--- timeline ---', '');

  for (const entry of timeline) {
    const t = new Date(entry.ts).toISOString();
    switch (entry.kind) {
      case 'transcript':
        lines.push(`[${t}] ${entry.role.toUpperCase()}: ${entry.text}`);
        break;
      case 'tool':
        lines.push(`[${t}] TOOL ${entry.status}: ${entry.name}`);
        break;
      case 'ui':
        lines.push(`[${t}] UI ${entry.action}: ${entry.detail}`);
        break;
      case 'approval':
        lines.push(`[${t}] APPROVAL ${entry.phase}: ${entry.tool} — ${entry.summary}`);
        break;
      case 'evidence': {
        const sourceCount = entry.sources?.length ?? 0;
        const textBit = entry.text.trim() || '(no text)';
        lines.push(
          `[${t}] EVIDENCE ${entry.callId}: ${textBit}` +
            (sourceCount > 0 ? ` [${sourceCount} sources]` : '')
        );
        break;
      }
      default: {
        const _exhaustive: never = entry;
        void _exhaustive;
      }
    }
  }

  if (timeline.length === 0) {
    lines.push('(empty)');
  }

  lines.push('');
  return lines.join('\n');
}

export function downloadVoiceDebugDump(text: string, filename?: string): void {
  const name =
    filename ?? `voice-session-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

const LAST_DUMP_STORAGE_KEY = 'voice-session-last-dump';

/**
 * On session end: in local/dev, persist + log the full dump (manual Download
 * button only — never auto-download). Elsewhere, metadata-only log (no PHI).
 */
export function autoDumpVoiceSession(
  timeline: TimelineEntry[],
  meta: VoiceDebugDumpMeta
): void {
  if (timeline.length === 0) return;

  if (!shouldAutoDumpVoiceSession()) {
    logger.info('[voice] session ended', {
      connection: meta.connection,
      sessionState: meta.sessionState,
      timelineEntries: timeline.length,
      surfacePath: meta.surfacePath,
      currentSessionId: meta.currentSessionId,
      hasError: Boolean(meta.error),
    });
    return;
  }

  const text = formatVoiceDebugDump(timeline, meta);
  try {
    sessionStorage.setItem(LAST_DUMP_STORAGE_KEY, text);
  } catch {
    // private mode / quota — ignore
  }
  logger.info('[voice] session dump\n' + text);
}
